import { memo, useCallback, useContext, useEffect, useRef, useState, type MouseEvent } from 'react';
import styled from '@emotion/styled';
import AssistantIcon from '@mui/icons-material/Assistant';
import { InputControlsContext } from '@trinity/chat-components/components/BrandInsights/context';
import { ChatStateDispatchContext } from '@trinity/chat-components/components/Chat.State/context';
import { Tooltip } from '../presentation';
import { TextHintIcon, TextHintWrap } from '../presentation/Message.QueryHint';
import { useChatTextInputControls } from './hooks/useChatTextInputControls';

type LearnMoreProps = {
  displayValue?: string;
  open: boolean;
  onDirectSubmit?: (question: string) => void;
  question: string;
  showDirectSubmit?: boolean;
};

export const LearnMore = memo(
  ({
    open: _open,
    question,
    displayValue = 'Learn More',
    showDirectSubmit = false,
    onDirectSubmit,
  }: LearnMoreProps) => {
    const dispatch = useContext(ChatStateDispatchContext);
    const inputControls = useContext(InputControlsContext);
    const { focusChatInputTextArea } = useChatTextInputControls();
    const [isLearnMoreHovered, setIsLearnMoreHovered] = useState(false);

    const textRef = useRef<HTMLDivElement>(null);

    const handleClick = useCallback(
      (e: MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (inputControls?.replaceInput) {
          inputControls.replaceInput(question);
        } else {
          dispatch({
            type: 'input-changed',
            payload: { value: question },
          });
        }
        focusChatInputTextArea();
      },
      [dispatch, focusChatInputTextArea, inputControls, question]
    );

    const handleDirectSubmit = useCallback(
      (e: MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        onDirectSubmit?.(question);
      },
      [onDirectSubmit, question]
    );

    const handleLearnMoreMouseEnter = useCallback(() => {
      setIsLearnMoreHovered(true);
    }, []);

    const handleLearnMoreMouseLeave = useCallback(() => {
      setIsLearnMoreHovered(false);
    }, []);

    useEffect(() => {
      const textEl = textRef.current;
      if (!textEl) {
        return;
      }

      const handleTransitionEnd = () => {
        textEl.style.opacity = '1';
      };

      const handleTransitionStart = () => {
        if (textRef.current) {
          textRef.current.style.opacity = '0';
        }
      };

      textEl.addEventListener('transitionstart', handleTransitionStart);
      textEl.addEventListener('transitionend', handleTransitionEnd);

      return () => {
        textEl.removeEventListener('transitionstart', handleTransitionStart);
        textEl.removeEventListener('transitionend', handleTransitionEnd);
      };
    }, []);

    const isExpanded = isLearnMoreHovered;
    const learnMoreWidth = isExpanded ? 95 : 22;

    return (
      <LearnMoreRoot>
        <StyledLearnMoreWrap
          onClick={handleClick}
          onMouseEnter={handleLearnMoreMouseEnter}
          onMouseLeave={handleLearnMoreMouseLeave}
          style={{
            width: learnMoreWidth,
            minWidth: learnMoreWidth,
            maxWidth: learnMoreWidth,
            flex: `0 0 ${learnMoreWidth.toString()}px`,
          }}
        >
          <StyledTextHintIcon />
          <LearnMoreText open={isExpanded} ref={textRef}>
            {isExpanded ? displayValue : ''}
          </LearnMoreText>
        </StyledLearnMoreWrap>
        {showDirectSubmit && onDirectSubmit && (
          <Tooltip title="Ask AIDA">
            <StyledAssistantIcon
              role="button"
              tabIndex={0}
              aria-label="Ask AIDA"
              onClick={handleDirectSubmit}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  e.stopPropagation();
                  onDirectSubmit(question);
                }
              }}
            />
          </Tooltip>
        )}
      </LearnMoreRoot>
    );
  }
);

const LearnMoreRoot = styled.div({
  display: 'inline-flex',
  alignItems: 'center',
  marginLeft: 5,
  gap: 4,
  width: 'auto',
});

const StyledLearnMoreWrap = styled(TextHintWrap)({
  fontSize: 13,
  padding: `0 1px 0 3px`,
  alignItems: 'center',
  height: 22,
  borderRadius: 5,
  transition: 'width .1s ease-in-out',
});

const LearnMoreText = styled.span<{ open: boolean }>(({ open }) => ({
  marginLeft: 3,
  opacity: 0,
  width: open ? 70 : 0,
  transition: 'width .1s ease-in-out',
  overflowX: 'hidden',
}));

const StyledTextHintIcon = styled(TextHintIcon)({
  marginRight: 0,
});

const StyledAssistantIcon = styled(AssistantIcon)(({ theme }) => ({
  fontSize: 18,
  marginRight: 0,
  color: theme.palette.primary.main,
  cursor: 'pointer',
}));
